/**
 * Theme Tokens API
 *
 * This module wraps the auto-generated tokens and provides a clean API
 * for components to use. Components should import from here, NOT from
 * variables.css directly.
 *
 * USAGE:
 *
 * 1. CSS Modules (preferred for static styles):
 *    ```css
 *    .button { background: var(--button-solid-primary-background); }
 *    ```
 *
 * 2. Inline styles with cssVar helper:
 *    ```tsx
 *    import { cssVar, vars } from '@/theme';
 *    <div style={{ color: cssVar(vars.colors.brand.primary) }} />
 *    ```
 *
 * 3. Direct token access:
 *    ```tsx
 *    import { tokens } from '@/theme';
 *    <div style={{ color: tokens.legacy.colorBrandPrimary }} />
 *    ```
 */

// =============================================================================
// RE-EXPORT RAW GENERATED TOKENS
// =============================================================================

export { default as tokens } from "./tokens.generated";
export type { Tokens } from "./tokens.generated";

// =============================================================================
// CSS VARIABLE HELPERS
// =============================================================================

/**
 * Creates a CSS var() reference from a variable name.
 * @param varName - CSS variable name (with or without --)
 * @returns CSS var() reference string
 *
 * @example
 * cssVar('color-brand-primary') // Returns: 'var(--color-brand-primary)'
 * cssVar('--color-brand-primary') // Returns: 'var(--color-brand-primary)'
 */
export function cssVar(varName: string): string {
    const name = varName.startsWith("--") ? varName : `--${varName}`;
    return `var(${name})`;
}

/**
 * Creates a CSS var() reference with a fallback value.
 * @param varName - CSS variable name
 * @param fallback - Fallback value if variable is not defined
 * @returns CSS var() reference with fallback
 *
 * @example
 * cssVarWithFallback('color-brand-primary', '#2d7b9f')
 * // Returns: 'var(--color-brand-primary, #2d7b9f)'
 */
export function cssVarWithFallback(varName: string, fallback: string): string {
    const name = varName.startsWith("--") ? varName : `--${varName}`;
    return `var(${name}, ${fallback})`;
}

// =============================================================================
// VARS OBJECT - Organized paths to CSS variable NAMES
// =============================================================================

/**
 * Organized references to CSS variable names.
 * Use with cssVar() helper for inline styles.
 *
 * @example
 * import { cssVar, vars } from '@/theme';
 * <div style={{ background: cssVar(vars.colors.brand.primary) }} />
 */
export const vars = {
    colors: {
        // Brand colors (teal palette)
        brand: {
            primary: "color-brand-primary",
            primaryHover: "color-brand-primary-hover",
            primaryLight: "color-brand-primary-light",
            primaryMuted: "color-brand-primary-muted"
        },
        // Accent colors (green palette)
        accent: {
            primary: "color-brand-accent",
            light: "color-brand-accent-light"
        },
        // Background colors
        background: {
            primary: "color-bg-primary",
            secondary: "color-bg-secondary",
            tertiary: "color-bg-tertiary",
            input: "color-bg-input",
            card: "color-bg-card",
            overlay: "color-bg-overlay"
        },
        // Text colors
        text: {
            primary: "color-text-primary",
            secondary: "color-text-secondary",
            muted: "color-text-muted",
            mutedTransparent: "color-text-muted-transparent",
            inverse: "color-text-inverse",
            link: "color-text-link"
        },
        // Border colors
        border: {
            default: "color-border-default",
            accent: "color-border-accent",
            accentLight: "color-border-accent-light",
            focus: "color-border-focus",
            subtle: "color-border-subtle"
        },
        // Feedback/status colors
        feedback: {
            error: "color-feedback-error",
            errorBg: "color-feedback-error-bg",
            success: "color-feedback-success",
            successBg: "color-feedback-success-bg",
            warning: "color-feedback-warning",
            warningBg: "color-feedback-warning-bg",
            info: "color-feedback-info",
            infoBg: "color-feedback-info-bg"
        },
        // Theme palette (raw teal values)
        theme: {
            darker: "theme-Darker",
            dark: "theme-Dark",
            darkAlt: "theme-DarkAlt",
            primary: "theme-Primary",
            secondary: "theme-Secondary",
            tertiary: "theme-Tertiary",
            light: "theme-Light",
            lighter: "theme-Lighter",
            lighterAlt: "theme-LighterAlt"
        },
        // Neutral palette
        neutral: {
            dark: "neutral-Dark",
            primary: "neutral-Primary",
            primaryAlt: "neutral-PrimaryAlt",
            secondary: "neutral-Secondary",
            secondaryAlt: "neutral-SecondaryAlt",
            tertiary: "neutral-Tertiary",
            tertiaryAlt: "neutral-TertiaryAlt",
            quaternary: "neutral-Quaternary",
            quaternaryAlt: "neutral-QuaternaryAlt",
            light: "neutral-Light",
            lighter: "neutral-Lighter",
            lighterAlt: "neutral-LighterAlt",
            white: "neutral-White"
        }
    },
    spacing: {
        xxs: "spacing-xxs",
        xs: "spacing-xs",
        sm: "spacing-sm",
        md: "spacing-md",
        lg: "spacing-lg",
        xl: "spacing-xl",
        xxl: "spacing-xxl",
        xxxl: "spacing-xxxl",
        // Fluent-style spacing
        horizontal: {
            none: "spacing-Horizontal-None",
            xxs: "spacing-Horizontal-XXS",
            xs: "spacing-Horizontal-XS",
            s: "spacing-Horizontal-S",
            m: "spacing-Horizontal-M",
            l: "spacing-Horizontal-L",
            xl: "spacing-Horizontal-XL",
            xxl: "spacing-Horizontal-XXL",
            xxxl: "spacing-Horizontal-XXXL"
        },
        vertical: {
            none: "spacing-Vertical-None",
            xxs: "spacing-Vertical-XXS",
            xs: "spacing-Vertical-XS",
            s: "spacing-Vertical-S",
            m: "spacing-Vertical-M",
            l: "spacing-Vertical-L",
            xl: "spacing-Vertical-XL",
            xxl: "spacing-Vertical-XXL",
            xxxl: "spacing-Vertical-XXXL"
        }
    },
    typography: {
        fontFamily: {
            primary: "font-family-primary",
            secondary: "font-family-secondary",
            base: "font-Family-Base",
            header: "font-Family-Header"
        },
        fontSize: {
            xs: "font-size-xs",
            sm: "font-size-sm",
            base: "font-size-base",
            md: "font-size-md",
            lg: "font-size-lg",
            xl: "font-size-xl",
            xxl: "font-size-xxl",
            xxxl: "font-size-xxxl",
            display: "font-size-display"
        },
        fontWeight: {
            light: "font-weight-light",
            regular: "font-weight-regular",
            medium: "font-weight-medium",
            semibold: "font-weight-semibold",
            bold: "font-weight-bold"
        },
        lineHeight: {
            base100: "lineHeight-Base-100",
            base200: "lineHeight-Base-200",
            base300: "lineHeight-Base-300",
            base400: "lineHeight-Base-400",
            base500: "lineHeight-Base-500",
            base600: "lineHeight-Base-600"
        }
    },
    radius: {
        none: "radius-none",
        sm: "radius-sm",
        md: "radius-md",
        lg: "radius-lg",
        xl: "radius-xl",
        xxl: "radius-xxl",
        pill: "radius-pill",
        circle: "radius-circle",
        // Fluent-style stroke radius
        stroke: {
            none: "stroke-Radius-None",
            small: "stroke-Radius-Small",
            medium: "stroke-Radius-Medium",
            large: "stroke-Radius-Large",
            xlarge: "stroke-Radius-XLarge",
            circular: "stroke-Radius-Circular"
        }
    },
    shadow: {
        none: "shadow-none",
        sm: "shadow-sm",
        md: "shadow-md",
        lg: "shadow-lg",
        navigation: "shadow-navigation",
        // Effect-style shadows
        resting: {
            small: "shadow--resting--small",
            medium: "shadow--resting--medium"
        },
        floating: {
            small: "shadow--floating--small",
            medium: "shadow--floating--medium"
        }
    },
    transition: {
        fast: "transition-fast",
        default: "transition-default",
        slow: "transition-slow"
    },
    zIndex: {
        base: "z-base",
        dropdown: "z-dropdown",
        sticky: "z-sticky",
        modal: "z-modal",
        popover: "z-popover",
        tooltip: "z-tooltip",
        toast: "z-toast"
    },
    // Component-specific tokens
    components: {
        nav: {
            widthCollapsed: "nav-width-collapsed",
            widthExpanded: "nav-width-expanded",
            background: "nav-background",
            foreground: "nav-foreground",
            hover: "nav-hover",
            active: "nav-active"
        },
        header: {
            height: "header-height",
            background: "header-background",
            foreground: "header-foreground",
            border: "header-border"
        },
        card: {
            background: "card-background",
            border: "card-border",
            radius: "card-radius",
            shadow: "card-shadow",
            padding: "card-padding"
        },
        input: {
            height: "input-height",
            background: "input-background",
            border: "input-border",
            borderFocus: "input-border-focus",
            radius: "input-radius",
            padding: "input-padding"
        },
        button: {
            solid: {
                primary: {
                    foreground: "button-solid-primary-foreground",
                    background: "button-solid-primary-background",
                    stroke: "button-solid-primary-stroke",
                    foregroundHover: "button-solid-primary-foreground-hover",
                    backgroundHover: "button-solid-primary-background-hover",
                    foregroundPressed: "button-solid-primary-foreground-pressed",
                    backgroundPressed: "button-solid-primary-background-pressed",
                    foregroundDisabled: "button-solid-primary-foreground-disabled",
                    backgroundDisabled: "button-solid-primary-background-disabled"
                },
                secondary: {
                    foreground: "button-solid-secondary-foreground",
                    background: "button-solid-secondary-background",
                    stroke: "button-solid-secondary-stroke",
                    foregroundHover: "button-solid-secondary-foreground-hover",
                    backgroundHover: "button-solid-secondary-background-hover"
                }
            },
            outlined: {
                primary: {
                    foreground: "button-outlined-primary-foreground",
                    background: "button-outlined-primary-background",
                    stroke: "button-outlined-primary-stroke",
                    foregroundHover: "button-outlined-primary-foreground-hover",
                    backgroundHover: "button-outlined-primary-background-hover"
                },
                secondary: {
                    foreground: "button-outlined-secondary-foreground",
                    background: "button-outlined-secondary-background",
                    stroke: "button-outlined-secondary-stroke"
                }
            },
            subtle: {
                primary: {
                    foreground: "button-subtle-primary-foreground",
                    background: "button-subtle-primary-background",
                    foregroundHover: "button-subtle-primary-foreground-hover",
                    backgroundHover: "button-subtle-primary-background-hover"
                },
                secondary: {
                    foreground: "button-subtle-secondary-foreground",
                    background: "button-subtle-secondary-background"
                },
                // Inverse variants for dark backgrounds
                primaryInverse: {
                    foreground: "button-subtle-primary-inverse-foreground",
                    background: "button-subtle-primary-inverse-background",
                    backgroundHover: "button-subtle-primary-inverse-background-hover"
                },
                secondaryInverse: {
                    foreground: "button-subtle-secondary-inverse-foreground",
                    background: "button-subtle-secondary-inverse-background",
                    backgroundHover: "button-subtle-secondary-inverse-background-hover"
                }
            }
        }
    }
} as const;

export type Vars = typeof vars;

// =============================================================================
// LEGACY EXPORTS - Backward compatible theme object
// =============================================================================

/**
 * Legacy theme object with pre-built var() references.
 * For backward compatibility with existing components.
 *
 * @deprecated Prefer using `cssVar(vars.x.y)` instead
 */
export const theme = {
    colors: {
        brand: {
            primary: cssVar(vars.colors.brand.primary),
            primaryHover: cssVar(vars.colors.brand.primaryHover),
            primaryLight: cssVar(vars.colors.brand.primaryLight),
            primaryMuted: cssVar(vars.colors.brand.primaryMuted)
        },
        accent: {
            primary: cssVar(vars.colors.accent.primary),
            light: cssVar(vars.colors.accent.light)
        },
        background: {
            primary: cssVar(vars.colors.background.primary),
            secondary: cssVar(vars.colors.background.secondary),
            tertiary: cssVar(vars.colors.background.tertiary),
            input: cssVar(vars.colors.background.input),
            card: cssVar(vars.colors.background.card),
            overlay: cssVar(vars.colors.background.overlay)
        },
        text: {
            primary: cssVar(vars.colors.text.primary),
            secondary: cssVar(vars.colors.text.secondary),
            muted: cssVar(vars.colors.text.muted),
            inverse: cssVar(vars.colors.text.inverse),
            link: cssVar(vars.colors.text.link)
        },
        border: {
            default: cssVar(vars.colors.border.default),
            accent: cssVar(vars.colors.border.accent),
            focus: cssVar(vars.colors.border.focus),
            subtle: cssVar(vars.colors.border.subtle)
        },
        feedback: {
            error: cssVar(vars.colors.feedback.error),
            errorBg: cssVar(vars.colors.feedback.errorBg),
            success: cssVar(vars.colors.feedback.success),
            successBg: cssVar(vars.colors.feedback.successBg),
            warning: cssVar(vars.colors.feedback.warning),
            warningBg: cssVar(vars.colors.feedback.warningBg),
            info: cssVar(vars.colors.feedback.info),
            infoBg: cssVar(vars.colors.feedback.infoBg)
        }
    },
    spacing: {
        xxs: cssVar(vars.spacing.xxs),
        xs: cssVar(vars.spacing.xs),
        sm: cssVar(vars.spacing.sm),
        md: cssVar(vars.spacing.md),
        lg: cssVar(vars.spacing.lg),
        xl: cssVar(vars.spacing.xl),
        xxl: cssVar(vars.spacing.xxl),
        xxxl: cssVar(vars.spacing.xxxl)
    },
    typography: {
        fontFamily: {
            primary: cssVar(vars.typography.fontFamily.primary),
            secondary: cssVar(vars.typography.fontFamily.secondary)
        },
        fontSize: {
            xs: cssVar(vars.typography.fontSize.xs),
            sm: cssVar(vars.typography.fontSize.sm),
            base: cssVar(vars.typography.fontSize.base),
            md: cssVar(vars.typography.fontSize.md),
            lg: cssVar(vars.typography.fontSize.lg),
            xl: cssVar(vars.typography.fontSize.xl),
            xxl: cssVar(vars.typography.fontSize.xxl)
        },
        fontWeight: {
            light: cssVar(vars.typography.fontWeight.light),
            regular: cssVar(vars.typography.fontWeight.regular),
            medium: cssVar(vars.typography.fontWeight.medium),
            semibold: cssVar(vars.typography.fontWeight.semibold),
            bold: cssVar(vars.typography.fontWeight.bold)
        }
    },
    radius: {
        none: cssVar(vars.radius.none),
        sm: cssVar(vars.radius.sm),
        md: cssVar(vars.radius.md),
        lg: cssVar(vars.radius.lg),
        xl: cssVar(vars.radius.xl),
        pill: cssVar(vars.radius.pill),
        circle: cssVar(vars.radius.circle)
    },
    shadow: {
        none: cssVar(vars.shadow.none),
        sm: cssVar(vars.shadow.sm),
        md: cssVar(vars.shadow.md),
        lg: cssVar(vars.shadow.lg),
        navigation: cssVar(vars.shadow.navigation)
    },
    transition: {
        fast: cssVar(vars.transition.fast),
        default: cssVar(vars.transition.default),
        slow: cssVar(vars.transition.slow)
    },
    zIndex: {
        base: cssVar(vars.zIndex.base),
        dropdown: cssVar(vars.zIndex.dropdown),
        sticky: cssVar(vars.zIndex.sticky),
        modal: cssVar(vars.zIndex.modal),
        popover: cssVar(vars.zIndex.popover),
        tooltip: cssVar(vars.zIndex.tooltip),
        toast: cssVar(vars.zIndex.toast)
    }
} as const;

export type Theme = typeof theme;

// =============================================================================
// FLAT LEGACY EXPORTS - For direct destructuring
// =============================================================================

/** @deprecated Use theme.colors or cssVar(vars.colors.x) instead */
export const colors = theme.colors;
export type Colors = typeof colors;

/** @deprecated Use theme.spacing or cssVar(vars.spacing.x) instead */
export const spacing = theme.spacing;
export type Spacing = typeof spacing;

/** @deprecated Use theme.typography or cssVar(vars.typography.x) instead */
export const typography = theme.typography;
export type Typography = typeof typography;

/** @deprecated Use theme.radius or cssVar(vars.radius.x) instead */
export const borderRadius = theme.radius;

/** @deprecated Use theme.shadow or cssVar(vars.shadow.x) instead */
export const shadows = theme.shadow;

/** @deprecated Use theme.transition or cssVar(vars.transition.x) instead */
export const transitions = theme.transition;

/** @deprecated Use theme.zIndex or cssVar(vars.zIndex.x) instead */
export const zIndex = theme.zIndex;

// =============================================================================
// COMPONENT TOKENS - Pre-built references for component styling
// =============================================================================

/**
 * Component-specific token references.
 * Use these for building component styles.
 */
export const components = {
    button: {
        solid: {
            primary: {
                color: cssVar(vars.components.button.solid.primary.foreground),
                background: cssVar(vars.components.button.solid.primary.background),
                borderColor: cssVar(vars.components.button.solid.primary.stroke),
                hoverBackground: cssVar(vars.components.button.solid.primary.backgroundHover),
                pressedBackground: cssVar(vars.components.button.solid.primary.backgroundPressed),
                disabledBackground: cssVar(vars.components.button.solid.primary.backgroundDisabled),
                disabledColor: cssVar(vars.components.button.solid.primary.foregroundDisabled)
            },
            secondary: {
                color: cssVar(vars.components.button.solid.secondary.foreground),
                background: cssVar(vars.components.button.solid.secondary.background),
                borderColor: cssVar(vars.components.button.solid.secondary.stroke),
                hoverBackground: cssVar(vars.components.button.solid.secondary.backgroundHover)
            }
        },
        outlined: {
            primary: {
                color: cssVar(vars.components.button.outlined.primary.foreground),
                background: cssVar(vars.components.button.outlined.primary.background),
                borderColor: cssVar(vars.components.button.outlined.primary.stroke),
                hoverBackground: cssVar(vars.components.button.outlined.primary.backgroundHover)
            },
            secondary: {
                color: cssVar(vars.components.button.outlined.secondary.foreground),
                background: cssVar(vars.components.button.outlined.secondary.background),
                borderColor: cssVar(vars.components.button.outlined.secondary.stroke)
            }
        },
        subtle: {
            primary: {
                color: cssVar(vars.components.button.subtle.primary.foreground),
                background: cssVar(vars.components.button.subtle.primary.background),
                hoverBackground: cssVar(vars.components.button.subtle.primary.backgroundHover)
            },
            secondary: {
                color: cssVar(vars.components.button.subtle.secondary.foreground),
                background: cssVar(vars.components.button.subtle.secondary.background)
            },
            primaryInverse: {
                color: cssVar(vars.components.button.subtle.primaryInverse.foreground),
                background: cssVar(vars.components.button.subtle.primaryInverse.background),
                hoverBackground: cssVar(vars.components.button.subtle.primaryInverse.backgroundHover)
            },
            secondaryInverse: {
                color: cssVar(vars.components.button.subtle.secondaryInverse.foreground),
                background: cssVar(vars.components.button.subtle.secondaryInverse.background),
                hoverBackground: cssVar(vars.components.button.subtle.secondaryInverse.backgroundHover)
            }
        }
    },
    nav: {
        width: {
            collapsed: cssVar(vars.components.nav.widthCollapsed),
            expanded: cssVar(vars.components.nav.widthExpanded)
        },
        background: cssVar(vars.components.nav.background),
        foreground: cssVar(vars.components.nav.foreground)
    },
    card: {
        background: cssVar(vars.components.card.background),
        border: cssVar(vars.components.card.border),
        radius: cssVar(vars.components.card.radius),
        shadow: cssVar(vars.components.card.shadow),
        padding: cssVar(vars.components.card.padding)
    },
    input: {
        height: cssVar(vars.components.input.height),
        background: cssVar(vars.components.input.background),
        border: cssVar(vars.components.input.border),
        borderFocus: cssVar(vars.components.input.borderFocus),
        radius: cssVar(vars.components.input.radius),
        padding: cssVar(vars.components.input.padding)
    }
} as const;

// =============================================================================
// BREAKPOINTS - Media query helpers
// =============================================================================

export const breakpoints = {
    xs: "320px",
    sm: "480px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    xxl: "1536px"
} as const;

export const mediaQueries = {
    xs: `@media (min-width: ${breakpoints.xs})`,
    sm: `@media (min-width: ${breakpoints.sm})`,
    md: `@media (min-width: ${breakpoints.md})`,
    lg: `@media (min-width: ${breakpoints.lg})`,
    xl: `@media (min-width: ${breakpoints.xl})`,
    xxl: `@media (min-width: ${breakpoints.xxl})`
} as const;

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default theme;
